import { useEffect, useRef } from 'react';
import Button from './Button';
import lineIcon from '../assets/line.svg';
import facebookIcon from '../assets/facebook.svg';
import googleIcon from '../assets/google.svg';
import * as auth from '../api/auth';
import useAuthStore from '../stores/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastProvider';
import { useTranslation } from 'react-i18next';

// Mock configuration for social providers
const socialProviderConfigs = {
  google: {
    clientId: '157231963660-vfcm7eq6d1aruog19ph2a8o7ncnhrdr4.apps.googleusercontent.com',
    // No explicit scope needed here for google.accounts.id.initialize for basic ID token
    // redirectUri is not used by google.accounts.id.initialize for popup/ID token flow
  },
  facebook: {
    clientId: '585346913926443', // Updated App ID
    redirectUri: `${window.location.origin}/auth/facebook/callback`,
    scope: 'email,public_profile', // Permissions to request
    version: 'v18.0' // Facebook Graph API version
  },
  line: {
    clientId: '2006931997', // LINE Channel ID for development/testing
    redirectUri: `${window.location.origin}/auth/line/callback`,
    scope: 'profile openid email', // LINE Login scopes
  }
};

const SocialLoginButtons = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const { toast } = useToast();
  const { t } = useTranslation();
  const googleButtonContainerRef = useRef(null); // Ref for the hidden GSI button container

  // Callback for Google Sign-In initiated by GSI (expects { credential: "..." })
  const handleGoogleCredentialResponse = async (response) => {
    console.log("Google Credential Response:", response);
    if (response && response.credential) {
      try {
        const backendResponse = await auth.socialLogin('google', response.credential);
        setAccessToken(backendResponse.accessToken);
        await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        toast(t('social_login.success'));
        navigate('/');
      } catch (err) {
        console.error('Google login via custom button (GSI credential) failed:', err);
        toast(t('social_login.errors.general'));
      }
    } else {
      console.error("Google Sign-In error: No credential in response or unexpected response format");
      toast(t('social_login.errors.general'));
    }
  };



  useEffect(() => {
    const gsiScriptId = 'google-gsi-script';
    if (!document.getElementById(gsiScriptId)) {
      const script = document.createElement('script');
      script.id = gsiScriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google GSI script loaded.");
        if (window.google && window.google.accounts && window.google.accounts.id) {
          window.google.accounts.id.initialize({
            client_id: socialProviderConfigs.google.clientId,
            callback: handleGoogleCredentialResponse,
            ux_mode: "popup",
          });
          if (googleButtonContainerRef.current) {
             window.google.accounts.id.renderButton(
              googleButtonContainerRef.current,
              { theme: 'outline', size: 'large', type: 'standard' }
            );
          }
        } else {
          console.error("Google GSI library loaded, but accounts.id not available.");
        }
      };
      document.head.appendChild(script);
    }


  }, []); // Empty dependency array to run once on mount

  const handleSocialLoginClick = (providerName) => {
    const provider = providerName.toLowerCase();
    const config = socialProviderConfigs[provider];

    if (!config) {
      console.error(`Configuration for ${providerName} not found.`);
      return;
    }

    if (provider === 'google') {
      // Find the GSI rendered button within our hidden container and click it
      if (googleButtonContainerRef.current) {
        const gsiButton = googleButtonContainerRef.current.querySelector('div[role="button"]');
        if (gsiButton) {
          gsiButton.click();
        } else {
          console.error('GSI button not found in hidden container. Ensure it rendered.');
          toast('Google Sign-In is not ready, please try again.');
          // As a fallback, or if renderButton wasn't called in useEffect yet:
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.log('Attempting to re-initialize and render GSI button for Google click');
            window.google.accounts.id.initialize({
              client_id: socialProviderConfigs.google.clientId,
              callback: handleGoogleCredentialResponse,
              ux_mode: "popup",
            });
            // Try to render and click immediately (might have timing issues)
            const tempDiv = document.createElement('div');
            window.google.accounts.id.renderButton(tempDiv, { type: 'standard' });
            const buttonInTemp = tempDiv.querySelector('div[role="button"]');
            if (buttonInTemp) buttonInTemp.click();
            else console.error("Fallback GSI button render/click failed");
          }
        }
      } else {
        console.error('Google button container ref not available.');
      }
    } else if (provider === 'facebook') {
      // Facebook Login OAuth 2.0 authorization flow
      const state = 'facebook_login_state_' + Math.random().toString(36).substring(2, 15); // Generate fresh state for CSRF protection

      // Save state in session storage for validation in callback
      const stateKey = `social_login_state_${provider}`;
      sessionStorage.setItem(stateKey, state);
      console.log(`Saved state for ${provider}:`, state);

      const authUrl = new URL(`https://www.facebook.com/${config.version}/dialog/oauth`);
      authUrl.searchParams.append('client_id', config.clientId);
      authUrl.searchParams.append('redirect_uri', config.redirectUri);
      authUrl.searchParams.append('scope', config.scope);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('state', state);

      console.log('Redirecting to Facebook Login:', authUrl.toString());
      window.location.href = authUrl.toString();
    } else if (provider === 'line') {
      // LINE Login OAuth 2.0 authorization flow
      const state = 'line_login_state_' + Math.random().toString(36).substring(2, 15); // Generate fresh state for CSRF protection

      // Save state in session storage for validation in callback
      const stateKey = `social_login_state_${provider}`;
      sessionStorage.setItem(stateKey, state);
      console.log(`Saved state for ${provider}:`, state);

      const authUrl = new URL('https://access.line.me/oauth2/v2.1/authorize');
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('client_id', config.clientId);
      authUrl.searchParams.append('redirect_uri', config.redirectUri);
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('scope', config.scope);

      console.log('Redirecting to LINE Login:', authUrl.toString());
      window.location.href = authUrl.toString();
    } else {
      // Fallback for other providers (mock implementation)
      const state = `${provider}_login_state_` + Math.random().toString(36).substring(2, 15);

      // Save state in session storage for validation in callback
      const stateKey = `social_login_state_${provider}`;
      sessionStorage.setItem(stateKey, state);
      console.log(`Saved state for ${provider}:`, state);

      const mockAuthCode = `mock_auth_code_for_${provider}`;
      const callbackUrl = `${config.redirectUri}?code=${mockAuthCode}&state=${state}`;
      window.location.href = callbackUrl;
    }
  };

  return (
    <>
      {/* Hidden container for the GSI button */}
      <div ref={googleButtonContainerRef} style={{ display: 'none' }}></div>

      <div className='flex items-center w-full gap-5 px-6'>
        <div className='flex-1 bg-[#dddddd] w-full h-[1px]' />
        <div className="text-center text-[#aaaaaa] text-sm my-4">
          {t('auth.login.social.divider')}
        </div>
        <div className='flex-1 bg-[#dddddd] w-full h-[1px]' />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { name: 'LINE', icon: lineIcon, provider: 'line' },
          { name: 'Facebook', icon: facebookIcon, provider: 'facebook' },
          { name: 'Google', icon: googleIcon, provider: 'google' }
        ].map(({ name, icon, provider }) => (
          <Button
            key={name}
            variant="default"
            size="xl"
            className="px-1 py-2.5 flex items-center justify-center gap-2 w-full"
            type="button"
            onClick={() => handleSocialLoginClick(name)}
          >
            <img src={icon} alt={name} className="w-6 h-6" />
            <span className="text-[#111111] text-base whitespace-nowrap">{name}</span>
          </Button>
        ))}
      </div>
    </>
  );
};

export default SocialLoginButtons; 